import React, { useEffect, useRef, useState } from "react";
import "./MicroMissionModal.css";

const fallbackMissionSteps = [
  {
    id: "fallback-step-1",
    number: 1,
    title: "Paso 1",
    blocks: [
      {
        id: "fallback-block-1",
        kind: "text",
        text: "Busca un lugar sin distracción. Respira, enfócate y aparta 10 minutos para entrar en modo ejecución.",
        placeholder: "",
      },
    ],
  },
  {
    id: "fallback-step-2",
    number: 2,
    title: "Paso 2",
    blocks: [
      {
        id: "fallback-block-2",
        kind: "question",
        text: "¿Qué estoy evitando hacer que sé que debo hacer?",
        placeholder: "Responde con honestidad",
      },
    ],
  },
  {
    id: "fallback-step-3",
    number: 3,
    title: "Paso 3",
    blocks: [
      {
        id: "fallback-block-3",
        kind: "complete_phrase",
        text: "Mi acción concreta de hoy será:",
        placeholder: "Define una acción posible y cerrable hoy",
      },
    ],
  },
  {
    id: "fallback-step-4",
    number: 4,
    title: "Paso 4",
    blocks: [
      {
        id: "fallback-block-4",
        kind: "text",
        text: "Haz la acción en el momento que definas. No la amplíes, no la cambies y no la dejes para después.",
        placeholder: "",
      },
    ],
  },
  {
    id: "fallback-step-5",
    number: 5,
    title: "Paso 5",
    blocks: [
      {
        id: "fallback-block-5a",
        kind: "rule",
        text: "Identifica una excusa, define una acción y ejecútala sin negociar.",
        placeholder: "Regla del día",
      },
      {
        id: "fallback-block-5b",
        kind: "complete_phrase",
        text: "Hoy dejé de justificar y ejecuté:",
        placeholder: "Completa tu evidencia",
      },
    ],
  },
];

const fallbackClosing = {
  title: "Altar de Testimonio",
  blocks: [
    {
      id: "fallback-closing-1",
      kind: "text",
      text: "Hoy añadiste una piedra de testimonio. No resolviste todo, pero gobernaste una decisión concreta.",
      placeholder: "",
    },
  ],
};

export default function MicroMissionModal({
  open,
  onClose,
  missionData,
  missionProgress,
  missionSummary,
  missionDaySummary,
  onComplete,
}) {

  const baseSteps = missionData?.steps?.length
    ? missionData.steps
    : fallbackMissionSteps;

  const closing = missionData?.closing || fallbackClosing;

  const steps = [
    ...baseSteps,
    {
      id: "mission-closing",
      number: baseSteps.length + 1,
      title: closing?.title || "Altar de Testimonio",
      isClosing: true,
      blocks: closing?.blocks?.length ? closing.blocks : fallbackClosing.blocks,
    },
  ];

  const [step, setStep] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);

  const [selectedAction, setSelectedAction] = useState("");
  const [selectionValues, setSelectionValues] = useState({});
  const [openSelectionId, setOpenSelectionId] = useState(null);
  const [binaryAnswers, setBinaryAnswers] = useState({});
  const [multiSelectAnswers, setMultiSelectAnswers] = useState({});
  const [expandedChoiceId, setExpandedChoiceId] = useState(null);
  const selectionInputRefs = useRef({});
  const missionShellRef = useRef(null);

  const [floatingTimerActive, setFloatingTimerActive] = useState(false);
  const [floatingTimerSeconds, setFloatingTimerSeconds] = useState(0);

  const [rewardInfoOpen, setRewardInfoOpen] = useState(false);
  const [savingMission, setSavingMission] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [missionAnswers, setMissionAnswers] = useState({});
  const [checklistAnswers, setChecklistAnswers] = useState({});

  const current = steps[step] || steps[0];
  const isLast = step === steps.length - 1;

  function scrollMissionToTop(behavior = "smooth") {
    requestAnimationFrame(() => {
      if (missionShellRef.current) {
        missionShellRef.current.scrollTo({
          top: 0,
          left: 0,
          behavior,
        });
      }
    });
  }

  const questionBlocks = current.blocks?.filter(
    (block) => block.kind === "question" || block.kind === "binary_question"
  ) || [];

  const hasQuestionSequence = questionBlocks.length > 1;
  const activeQuestion = questionBlocks[questionIndex];

  function shouldRenderMissionBlock(block) {
    if (!hasQuestionSequence) return true;

    if (block.kind === "question" || block.kind === "binary_question") {
      return block.id === activeQuestion?.id;
    }

    return true;
  }

  const completedRows = Math.min(step, 5);
  const altarRows = [5, 4, 3, 2, 1];

  const missionAlreadyCompleted = Boolean(missionProgress?.id);

  const dayTotalStones = Number(missionDaySummary?.total_stones_until_day || 0);

  const previousTotalStones = missionAlreadyCompleted
    ? Math.max(dayTotalStones - Number(missionProgress?.stones_earned || 120), 0)
    : dayTotalStones;

  const stepRewards = [10, 40, 20, 0, 50];

  const currentStepReward = current?.isClosing
    ? 0
    : stepRewards[step] ?? 0;

  const accumulatedTodayBeforeCurrentStep = current?.isClosing
    ? stepRewards.reduce((sum, value) => sum + value, 0)
    : stepRewards
        .slice(0, step)
        .reduce((sum, value) => sum + value, 0);

  const accumulatedTotalBeforeCurrentStep =
    previousTotalStones + accumulatedTodayBeforeCurrentStep;

  const currentClosingBonus = current?.isClosing ? 1 : 0;

  const completedFocusDays =
    Number(missionSummary?.completed_focus_days || 0) + currentClosingBonus;

  const completedSmallActions =
    Number(missionSummary?.completed_small_actions || 0) + currentClosingBonus;

  const evidenceStreakDays =
  Number(missionSummary?.evidence_streak_days || 0) + currentClosingBonus;

  const completedWeeks = Number(missionSummary?.completed_weeks || 0);

  const authoritySeals = [
    {
      key: "silence",
      name: "Silencio",
      short: "S",
      requirement: "7 días completando el enfoque",
      unlocked: completedFocusDays >= 7,
    },
    {
      key: "seed",
      name: "Semilla",
      short: "Se",
      requirement: "15 acciones pequeñas sin negociar",
      unlocked: completedSmallActions >= 15,
    },
    {
      key: "firm_altar",
      name: "Altar Firme",
      short: "AF",
      requirement: "14 días registrando evidencia",
      unlocked: evidenceStreakDays >= 14,
    },
    {
      key: "identity",
      name: "Identidad",
      short: "I",
      requirement: "Semana 3 completada",
      unlocked: completedWeeks >= 3,
    },
  ];

 const timerStepIndex = baseSteps.findIndex((missionStep) =>
    (missionStep.blocks || []).some((block) => block.kind === "timer")
  );

  const timerBlock =
    timerStepIndex >= 0
      ? (baseSteps[timerStepIndex].blocks || []).find(
          (block) => block.kind === "timer"
        )
      : null;

  const timerMinutes = Number(timerBlock?.text) || 10;
  const hasTimer = Boolean(timerBlock) && step >= timerStepIndex;

  useEffect(() => {
  if (open) {
    const alreadyCompleted = Boolean(missionProgress?.id);

    setStep(alreadyCompleted ? steps.length - 1 : 0);
    setQuestionIndex(0);
    setSelectedAction("");
    setSelectionValues({});
    setOpenSelectionId(null);
    setExpandedChoiceId(null);
    setBinaryAnswers({});
    setMultiSelectAnswers({});
    setChecklistAnswers({});
    setMissionAnswers({});
    setValidationError("");
    setSavingMission(false);

    setTimeout(() => {
      scrollMissionToTop("auto");
    }, 0);
  }
}, [open, missionProgress?.id]);

  useEffect(() => {
    setQuestionIndex(0);
    setExpandedChoiceId(null);
  }, [step]);

  useEffect(() => {
    if (!open) return;

    scrollMissionToTop("smooth");
  }, [step, questionIndex, open]);

  useEffect(() => {
    if (!open || !hasTimer) return;

    setFloatingTimerSeconds(timerMinutes * 60);
    setFloatingTimerActive(false);
  }, [open, hasTimer, timerMinutes]);

  useEffect(() => {
    if (!floatingTimerActive || floatingTimerSeconds <= 0) return;

    const interval = setInterval(() => {
      setFloatingTimerSeconds((value) => Math.max(value - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [floatingTimerActive, floatingTimerSeconds]);

  useEffect(() => {
    if (!openSelectionId) return;

    function handleCloseSelection(event) {
      const activeSelection = document.querySelector(
        `[data-selection-id="${openSelectionId}"]`
      );

      if (activeSelection && activeSelection.contains(event.target)) {
        return;
      }

      setOpenSelectionId(null);
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setOpenSelectionId(null);
      }
    }

    document.addEventListener("mousedown", handleCloseSelection);
    document.addEventListener("touchstart", handleCloseSelection);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleCloseSelection);
      document.removeEventListener("touchstart", handleCloseSelection);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openSelectionId]);
  
  if (!open) return null;

  function formatMissionInlineBold(text = "") {
    const parts = String(text || "").split(/(\*\*.*?\*\*)/g).filter(Boolean);

    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }

      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  }

  function parseChoiceCards(text = "") {
  return String(text || "")
    .split(/\n\s*---+\s*\n/g)
    .map((section) => section.trim())
    .filter(Boolean)
    .map((section, index) => {
      const lines = section
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

      const title = lines[0] || `Opción ${index + 1}`;
      const description = lines.slice(1).join("\n");

      return {
        id: `choice-card-${index}`,
        title,
        description,
        value: description ? `${title}: ${description}` : title,
      };
    });
}

  function splitLines(text = "") {
    return text
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function renderParagraphText(text = "") {
    return String(text || "")
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((paragraph, index) => (
        <p key={index} className="missionText">
          {formatMissionInlineBold(paragraph)}
        </p>
      ));
  }

function updateMissionAnswer(key, value) {
  setMissionAnswers((current) => ({
    ...current,
    [key]: value,
  }));
}

function getMissionAnswer(key) {
  return missionAnswers[key] || "";
}

function hasTextValue(value) {
  return String(value || "").trim().length > 0;
}

function blockRequiresEvidence(block) {
  if (!block) return false;

  if (block.kind === "text" || block.kind === "rule" || block.kind === "timer") {
    return false;
  }

  return true;
}

function blockHasEvidence(block) {
  if (!blockRequiresEvidence(block)) return true;

  if (block.kind === "question" || block.kind === "response") {
    return hasTextValue(getMissionAnswer(block.id));
  }

  if (block.kind === "binary_question") {
    return hasTextValue(binaryAnswers[block.id]);
  }

  if (block.kind === "example" || block.kind === "selection" || block.kind === "choice_cards") {
    return (
      hasTextValue(getSelectionValue(block)) ||
      hasTextValue(selectionValues[block.id]) ||
      hasTextValue(getMissionAnswer(block.id)) ||
      hasTextValue(getMissionAnswer("selected_action")) ||
      hasTextValue(selectedAction)
    );
  }

  if (block.kind === "multi_select") {
    return Array.isArray(multiSelectAnswers[block.id]) && multiSelectAnswers[block.id].length > 0;
  }

  if (block.kind === "checklist") {
    return Array.isArray(checklistAnswers[block.id]) && checklistAnswers[block.id].length > 0;
  }

  if (block.kind === "complete_phrase") {
    const normalizedText = String(block.text || "").toLowerCase();

    const isHiddenActionField =
      normalizedText.includes("acción concreta") ||
      normalizedText.includes("mi acción");

    if (isHiddenActionField) {
      return true;
    }

    return hasTextValue(getMissionAnswer(block.id)) || hasTextValue(selectedAction);
  }

  return true;
}

function currentStepHasEvidence() {
  const visibleBlocks = (current.blocks || []).filter(shouldRenderMissionBlock);
  const requiredBlocks = visibleBlocks.filter(blockRequiresEvidence);

  if (!requiredBlocks.length) return true;

  const selectionBlocks = requiredBlocks.filter(
    (block) =>
      block.kind === "example" ||
      block.kind === "selection" ||
      block.kind === "choice_cards"
  );

  const otherRequiredBlocks = requiredBlocks.filter(
    (block) =>
      block.kind !== "example" &&
      block.kind !== "selection" &&
      block.kind !== "choice_cards"
  );

  const selectionIsValid =
    selectionBlocks.length === 0 ||
    selectionBlocks.some(blockHasEvidence);

  const otherBlocksAreValid = otherRequiredBlocks.every(blockHasEvidence);

  return selectionIsValid && otherBlocksAreValid;
}

 function getValidationMessage() {
   return "Completa este paso para levantar evidencia real.";
 }

  function isActionSelection(block) {
    const text = `${block.placeholder || ""} ${block.text || ""}`.toLowerCase();

    return (
      text.includes("acción") ||
      text.includes("accion") ||
      text.includes("micro-acción") ||
      text.includes("micro accion")
    );
  }

  function getSelectionValue(block) {
    if (isActionSelection(block)) {
      return selectedAction;
    }

    return selectionValues[block.id] || "";
  }

  function updateSelectionValue(block, value) {
    const normalizedValue = String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    const isCustomActionOption =
      normalizedValue.includes("escribir mi propia accion") ||
      normalizedValue.includes("propia accion");

    if (isCustomActionOption) {
      setSelectedAction("");

      setSelectionValues((currentValues) => ({
        ...currentValues,
        [block.id]: "",
      }));

      updateMissionAnswer("selected_action", "");
      updateMissionAnswer(block.id, "");

      return;
    }

    if (isActionSelection(block)) {
      setSelectedAction(value);
      updateMissionAnswer("selected_action", value);
      setValidationError("");
      return;
    }

    setSelectionValues((currentValues) => ({
      ...currentValues,
      [block.id]: value,
    }));

    updateMissionAnswer(block.id, value);
    setValidationError("");
  }

  function renderMissionBlock(block) {
    if (!block?.text?.trim()) return null;

    if (block.kind === "binary_question") {
      const currentAnswer = binaryAnswers[block.id] || "";

      return (
        <div key={block.id} className="missionBinaryQuestion">
          {block.placeholder && <small>{block.placeholder}</small>}

          {block.text && (
            <p className="missionBinaryQuestionText">
              {formatMissionInlineBold(block.text)}
            </p>
          )}

          <div className="missionBinaryOptions">
            <button
              type="button"
              className={`missionBinaryBtn ${
                currentAnswer === "yes" ? "is-selected" : ""
              }`}
              onClick={() => {
                setBinaryAnswers((current) => ({
                  ...current,
                  [block.id]: "yes",
                }));
                updateMissionAnswer(block.id, "yes");
                setValidationError("");
              }}
            >
              Sí
            </button>

            <button
              type="button"
              className={`missionBinaryBtn ${
                currentAnswer === "no" ? "is-selected" : ""
              }`}
              onClick={() => {
                setBinaryAnswers((current) => ({
                  ...current,
                  [block.id]: "no",
                }));
                updateMissionAnswer(block.id, "no");
                setValidationError("");
              }}
            >
              No
            </button>
          </div>
        </div>
      );
    }

    if (block.kind === "question" || block.kind === "response") {
      return (
        <label key={block.id} className="missionField">
          {block.placeholder && <small>{block.placeholder}</small>}
          {block.text && <span>{formatMissionInlineBold(block.text)}</span>}
          <textarea
            rows="4"
            placeholder="Escribe aquí..."
            value={getMissionAnswer(block.id)}
            onChange={(e) => {
              updateMissionAnswer(block.id, e.target.value);
              setValidationError("");
            }}
          />
        </label>
      );
    }

    if (block.kind === "choice_cards") {
      const cards = parseChoiceCards(block.text);
      const selectedValue = getSelectionValue(block);

      return (
        <div key={block.id} className="missionChoiceCards">
          {block.placeholder && <small>{block.placeholder}</small>}

          <div className="missionChoiceCardsGrid">
            {cards.map((card) => {
              const choiceId = `${block.id}-${card.id}`;
              const isSelected = selectedValue === card.value;
              const isExpanded = expandedChoiceId === choiceId || isSelected;

              return (
                <div
                  key={card.id}
                  role="button"
                  tabIndex={0}
                  className={`missionChoiceCard ${
                    isSelected ? "is-selected" : ""
                  } ${isExpanded ? "is-expanded" : ""}`}
                  onClick={() => {
                    setExpandedChoiceId(isExpanded && !isSelected ? null : choiceId);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setExpandedChoiceId(isExpanded && !isSelected ? null : choiceId);
                    }
                  }}
                >
                  <strong>{formatMissionInlineBold(card.title)}</strong>

                  {isExpanded && card.description && (
                    <div className="missionChoiceCardText">
                      {renderParagraphText(card.description)}
                    </div>
                  )}

                  {isExpanded && (
                    <button
                      type="button"
                      className="missionChoiceCardAction"
                      onClick={(event) => {
                        event.stopPropagation();
                        updateSelectionValue(block, card.value);
                        setExpandedChoiceId(choiceId);
                      }}
                    >
                      {isSelected ? "Acción elegida" : "Elegir esta acción"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (block.kind === "example" || block.kind === "selection") {
      const options = splitLines(block.text);
      const value = getSelectionValue(block);
      const isOpen = openSelectionId === block.id;

      return (
        <div
           key={block.id}
           className="missionActionCombobox"
           data-selection-id={block.id}
         >
           {block.placeholder && <small>{block.placeholder}</small>}

           <div className="missionActionSelectWrap">
             <textarea
               ref={(element) => {
                 selectionInputRefs.current[block.id] = element;

                 if (element) {
                   element.style.height = "auto";
                   element.style.height = `${element.scrollHeight}px`;
                 }
               }}
               value={value}
               rows={2}
               placeholder="Escribe o elige una opción"
               onFocus={() => setOpenSelectionId(null)}
               onChange={(e) => {
                 updateSelectionValue(block, e.target.value);

                 e.target.style.height = "auto";
                 e.target.style.height = `${e.target.scrollHeight}px`;
               }}
             />

             <button
               type="button"
               className="missionActionSelectBtn"
               onClick={() =>
                 setOpenSelectionId((currentId) =>
                   currentId === block.id ? null : block.id
                 )
               }
               aria-label="Ver opciones"
             >
               ▾
             </button>
           </div>

           {isOpen && (
             <div className="missionActionDropdown">
               {options.map((option, index) => (
                 <button
                   key={index}
                   type="button"
                   onClick={() => {
                     updateSelectionValue(block, option);
                     setOpenSelectionId(null);

                     requestAnimationFrame(() => {
                       const element = selectionInputRefs.current[block.id];

                       if (element) {
                         const normalizedOption = String(option || "")
                           .trim()
                           .toLowerCase()
                           .normalize("NFD")
                           .replace(/[\u0300-\u036f]/g, "");

                         const isCustomActionOption =
                            normalizedOption.includes("escribir mi propia accion") ||
                            normalizedOption.includes("propia accion");

                         if (isCustomActionOption) {
                           element.value = "";
                         }

                         element.focus();
                         element.style.height = "auto";
                         element.style.height = `${element.scrollHeight}px`;
                       }
                     });
                   }}
                 >
                   {option}
                 </button>
               ))}

               <button
                 type="button"
                 className="missionActionDropdownCustom"
                 onClick={() => {
                  updateSelectionValue(block, "");
                   setSelectedAction("");

                   setSelectionValues((currentValues) => ({
                     ...currentValues,
                     [block.id]: "",
                   }));

                   setOpenSelectionId(null);

                   requestAnimationFrame(() => {
                     const element = selectionInputRefs.current[block.id];

                     if (element) {
                       element.value = "";
                       element.focus();
                       element.style.height = "auto";
                       element.style.height = `${element.scrollHeight}px`;
                      }
                    });
                 }}
                 >
                  Escribir mi propia acción
               </button>
             </div>
           )}
         </div>
       );
    }

    if (block.kind === "multi_select") {
      const options = splitLines(block.text);
      const selectedValues = multiSelectAnswers[block.id] || [];

      function toggleOption(option) {
        const isSelected = selectedValues.includes(option);

        const nextValues = isSelected
          ? selectedValues.filter((item) => item !== option)
          : [...selectedValues, option];

        setMultiSelectAnswers((current) => ({
          ...current,
          [block.id]: nextValues,
        }));

        updateMissionAnswer(block.id, nextValues);
      }

      return (
        <div key={block.id} className="missionMultiSelect">
          {block.placeholder && <small>{block.placeholder}</small>}

          <div className="missionMultiSelectList">
            {options.map((option, index) => {
              const isSelected = selectedValues.includes(option);

              return (
                <button
                  key={index}
                  type="button"
                  className={`missionMultiSelectOption ${
                    isSelected ? "is-selected" : ""
                  }`}
                  onClick={() => toggleOption(option)}
                >
                  <span className="missionMultiSelectMark">
                    {isSelected ? "✓" : ""}
                  </span>

                  <span className="missionMultiSelectText">
                    {formatMissionInlineBold(option)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (block.kind === "checklist") {
      const items = splitLines(block.text);
      const selectedItems = checklistAnswers[block.id] || [];

      function toggleChecklistItem(item) {
        const nextItems = selectedItems.includes(item)
          ? selectedItems.filter((currentItem) => currentItem !== item)
          : [...selectedItems, item];

        setChecklistAnswers((current) => ({
          ...current,
          [block.id]: nextItems,
        }));

        updateMissionAnswer(block.id, nextItems);
        setValidationError("");
      }

      return (
        <div key={block.id} className="missionChecklist">
          <p>{block.placeholder || "Checklist"}</p>

          <ul>
            {items.map((item, index) => {
              const isSelected = selectedItems.includes(item);

              return (
                <li key={index}>
                  <button
                    type="button"
                    className={`missionChecklistBtn ${isSelected ? "is-selected" : ""}`}
                    onClick={() => toggleChecklistItem(item)}
                  >
                    <span>{isSelected ? "✓" : ""}</span>
                    {item}
                 </button>
               </li>
              );
            })}
          </ul>
        </div>
      );
    }

    if (block.kind === "complete_phrase") {
      const normalizedText = block.text?.toLowerCase() || "";

      const isActionField =
        normalizedText.includes("acción concreta") ||
        normalizedText.includes("mi acción");

      if (isActionField) {
        return null;
      }

      const isEvidenceField =
        normalizedText.includes("hoy dejé") ||
        normalizedText.includes("ejecuté");

      if (isEvidenceField) {
        return (
          <label key={block.id} className="missionInlineEvidence">
            {block.placeholder && <small>{block.placeholder}</small>}

            <div className="missionInlineEvidenceRow">
              <span>{formatMissionInlineBold(block.text)}</span>
              <input
                type="text"
                placeholder="lo que ejecutaste hoy"
                value={getMissionAnswer(block.id)}
                onChange={(e) => {
                  updateMissionAnswer(block.id, e.target.value);
                  setValidationError("");
                }}
              />
            </div>
          </label>
        );
      }

      return (
        <label key={block.id} className="missionField missionField--phrase">
          {block.placeholder && <small>{block.placeholder}</small>}
          <span>{formatMissionInlineBold(block.text)}</span>

          <textarea
            rows="3"
            placeholder="Escribe aquí..."
            value={getMissionAnswer(block.id)}
            onChange={(e) => {
              updateMissionAnswer(block.id, e.target.value);

              if (isActionField) {
                setSelectedAction(e.target.value);
              }

              setValidationError("");
            }}
          />
        </label>
      );
    }

    if (block.kind === "timer") {
      return null;
    }

    if (block.kind === "rule") {
      return (
        <div key={block.id} className="missionRule missionRule--animated">
          <strong>{block.placeholder || "Regla del día"}</strong>
          <div className="missionRuleText">
            {renderParagraphText(block.text)}
          </div>
        </div>
      );
    }

    return (
      <div key={block.id} className="missionTextGroup">
        {renderParagraphText(block.text)}
      </div>
    );
  }

  function buildJournalEntries() {
    const entries = [];

    function addEntry(block, answer) {
      if (!block || answer === undefined || answer === null) return;

      const cleanAnswer = Array.isArray(answer)
         ? answer.filter(Boolean).join(", ")
        : typeof answer === "object"
        ? JSON.stringify(answer)
        : String(answer || "").trim();

      if (!cleanAnswer) return;

      entries.push({
        block_id: block.id,
        kind: block.kind,
        question: block.text || block.placeholder || "Registro de la micro-misión",
        answer: cleanAnswer,
      });
    }

    baseSteps.forEach((missionStep) => {
      (missionStep.blocks || []).forEach((block) => {
        if (block.kind === "text" || block.kind === "timer") {
          return;
        }

        if (block.kind === "rule") {
          const ruleText = String(block.text || "").trim();

          if (ruleText) {
            entries.push({
              block_id: block.id,
              kind: "rule",
              question: "Regla del día",
              answer: ruleText,
            });
          }

          return;
        }

        if (block.kind === "question" || block.kind === "response") {
          addEntry(block, missionAnswers[block.id]);
          return;
        }

        if (block.kind === "binary_question") {
          const value = binaryAnswers[block.id];

          if (value === "yes") addEntry(block, "Sí");
          if (value === "no") addEntry(block, "No");

          return;
        }

        if (
          block.kind === "selection" ||
          block.kind === "example" ||
          block.kind === "choice_cards"
        ) {
          const selectedValue =
            selectionValues[block.id] ||
            missionAnswers[block.id] ||
            "";

          const blockText = String(block.text || "").trim();
          const actionText = String(selectedAction || "").trim();

          const isThisSelected =
            selectedValue ||
            (actionText &&
              (actionText === blockText ||
                actionText.includes(blockText) ||
                blockText.includes(actionText)));

          if (!isThisSelected) return;

          const stepQuestionBlock = (missionStep.blocks || []).find(
            (item) =>
              item.id !== block.id &&
              item.kind === "text" &&
              String(item.text || "").trim()
          );

            entries.push({
            block_id: block.id,
            kind: block.kind,
            question:
              stepQuestionBlock?.text ||
              block.placeholder ||
              "Selecciona una opción:",
            answer: selectedValue || actionText || blockText,
          });

          return;
        }

        if (block.kind === "multi_select") {
          addEntry(block, multiSelectAnswers[block.id]);
          return;
        }

        if (block.kind === "checklist") {
          addEntry(block, checklistAnswers[block.id]);
          return;
        }

        if (block.kind === "complete_phrase") {
          addEntry(block, missionAnswers[block.id] || selectedAction);
        }
      });
    });

    return entries;
  }

  async function handlePrimaryAction() {
    if (isLast) {
    onClose();
    return;
   }

    setValidationError("");

    if (!currentStepHasEvidence()) {
      setValidationError(getValidationMessage());
      return;
    }

    if (hasQuestionSequence && questionIndex < questionBlocks.length - 1) {
      setQuestionIndex((value) => value + 1);
      return;
    }

    if (step === baseSteps.length - 1) {
      try {
        setSavingMission(true);

        if (onComplete) {
          await onComplete({
            journal_entries: buildJournalEntries(),
            selectedAction,
            selectionValues,
            binaryAnswers,
            multiSelectAnswers,
            checklistAnswers,
            missionAnswers,
          });
        }

        setStep((value) => Math.min(value + 1, steps.length - 1));
        return;
      } catch (err) {
        console.error("[MISSION_SAVE_ERROR]", err);
        setValidationError("No se pudo guardar la micro-misión. Intenta nuevamente.");
        return;
      } finally {
        setSavingMission(false);
      }
    }

    setStep((value) => Math.min(value + 1, steps.length - 1));
  }

function getPrimaryButtonText() {
  if (isLast) return "Volver al día";

  if (step === 0) {
    return "Hecho";
  }

  if (hasQuestionSequence && questionIndex < questionBlocks.length - 1) {
    return "Siguiente pregunta";
  }

  if (step === baseSteps.length - 1) {
    return "Levantar Altar";
  }

  return "Hecho";
}

function getStepMechanicText() {
  if (current?.isClosing) {
    return "Al levantar el Altar, se consolida la evidencia del día y se registra el acumulado alcanzado en esta micro-misión.";
  }

  if (step === 0) {
    return "Al completar este paso, preparas tu enfoque y se habilitan las primeras +10 Piedras de Testimonio para tu Altar.";
  }

  if (step === 1) {
    return "Cada respuesta honesta añade peso a tu evidencia. Este paso puede otorgar hasta +40 Piedras de Testimonio.";
  }

  if (step === 2) {
    return "Al definir una acción concreta y cerrable hoy, conviertes la intención en compromiso. Este paso otorga +20 Piedras de Testimonio.";
  }

  if (step === 3) {
    return "Al aceptar el diseño, sellas tu compromiso interno. Este paso no suma piedras, pero afirma la postura para ejecutar sin negociar.";
  }

  if (step === 4) {
    return "Al registrar evidencia real, declaras lo que efectivamente ejecutaste. Este paso otorga +50 Piedras de Testimonio.";
  }

  return "Cada paso completado levanta evidencia y fortalece tu Altar de Testimonio.";
}

function formatFloatingTimer(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(
    remainingSeconds
  ).padStart(2, "0")}`;
}

 return (
  <div className="missionOverlay" role="dialog" aria-modal="true">
    <div className="missionBackdrop" onClick={onClose} />

    <section className="missionShell" ref={missionShellRef}>
      {hasTimer && (
        <div className="missionFloatingTimer">
          <strong>{formatFloatingTimer(floatingTimerSeconds)}</strong>

          <button
            type="button"
            onClick={() => setFloatingTimerActive(true)}
            disabled={floatingTimerActive}
          >
            Iniciar
          </button>

          <button
            type="button"
            onClick={() => setFloatingTimerActive(false)}
            disabled={!floatingTimerActive}
          >
            Pausar
          </button>

          <button
            type="button"
            onClick={() => {
              setFloatingTimerActive(false);
              setFloatingTimerSeconds(timerMinutes * 60);
            }}
          >
            Reiniciar
          </button>
        </div>
      )}

      <button className="missionClose" type="button" onClick={onClose}>
        ×
      </button>

      <div className="missionHeader">
        <h2>Micro-misión del día</h2>
        <p>
          Convierte la verdad de hoy en una acción concreta y añade evidencia
          a tu Altar de Testimonio.
        </p>
      </div>

      <div className="missionFocusGrid">
        <div
          className={`missionAltarPanel ${
            current.isClosing ? "missionAltarPanel--closing" : ""
          }`}
        >
          <div
            className={`missionAltarStage ${
              current.isClosing ? "missionAltarStage--closing" : ""
            }`}
          >
            <div className="missionAltarAura" />

            <div className="missionAltarLightRays">
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>

            <div className="missionAltarStack">
              {altarRows.slice(0, completedRows).map((stoneCount, rowIndex) => (
                <div key={rowIndex} className="missionAltarRow is-active">
                  {Array.from({ length: stoneCount }).map((_, stoneIndex) => (
                    <span
                      key={stoneIndex}
                      className={`missionAltarStone missionAltarStone--${
                        ((rowIndex + stoneIndex) % 4) + 1
                      }`}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div
            className={`missionAltarCaption ${
              current.isClosing ? "missionAltarCaption--closing" : ""
            }`}
          >
            <h4>Altar de Testimonio</h4>
            <p>Tu constancia diaria edifica un testimonio inquebrantable.</p>

            {!current?.isClosing && (
              <div className="missionRewardStrip">
                <div>
                  <span>Acción</span>
                  <strong>
                    {currentStepReward > 0
                      ? `+${currentStepReward} Piedras`
                      : "Sellado"}
                  </strong>
                </div>

                <div>
                  <span>Acumulado</span>
                  <strong>{accumulatedTotalBeforeCurrentStep} Piedras</strong>
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className={`missionCard ${
            openSelectionId ? "missionCard--selectionOpen" : ""
          }`}
        >
          <div className="missionStepHeader">
             <div>
               <p className="missionStepLabel">
                 {current.isClosing
                   ? "Cierre final"
                   : `Micro-misión (${current.number} de ${baseSteps.length})`}
               </p>

               {hasQuestionSequence && (
                 <p className="missionQuestionCounter">
                   Pregunta {questionIndex + 1} de {questionBlocks.length}
                 </p>
               )}
             </div>

             <div className="missionStepInfoWrap">
               <button
                 type="button"
                 className="missionStepInfoBtn"
                 aria-label="Ver mecánica del paso"
               >
                 i
               </button>

               <div className="missionStepTooltip">
                 <strong>Mecánica del juego</strong>
                 <p>{getStepMechanicText()}</p>
              </div>
             </div>
           </div>

          <div className="missionDynamicBlocks">
            <div
              key={`${step}-${questionIndex}`}
              className="missionQuestionTransition"
            >
              {current.blocks?.length ? (
                current.blocks
                  .filter((block) => shouldRenderMissionBlock(block))
                  .map((block) => renderMissionBlock(block))
              ) : (
                <p className="missionText">
                  Este paso aún no tiene contenido configurado.
                </p>
              )}
            </div>
          </div>

          {current.isClosing && (
            <div className="missionClosingPanel">
              <div className="missionClosingHeader">
                <strong>Altar Levantado</strong>
                <p>
                  “La disciplina no es un castigo; es el marco que protege tu diseño
                  original.”
                </p>
              </div>

              <div className="missionClosingSummary">
                <div>
                  <span>Ganado hoy</span>
                  <strong>+120 Piedras</strong>
                </div>

                <div>
                  <span>Acumulado total</span>
                  <strong>{accumulatedTotalBeforeCurrentStep} Piedras</strong>
                </div>
              </div>

              <div className="missionSealsBlock">
                <div className="missionSealsHeader">
                  <span>Sellos de Autoridad</span>

                  <div className="missionStepInfoWrap">
                    <button
                      type="button"
                      className="missionStepInfoBtn"
                      aria-label="Ver requisitos de los sellos"
                    >
                      i
                    </button>

                    <div className="missionStepTooltip missionStepTooltip--seals">
                      <strong>Sellos de Autoridad</strong>

                      <ul>
                        <li>
                          <b>Silencio:</b> 7 días completando el enfoque.
                        </li>
                        <li>
                          <b>Semilla:</b> 15 acciones pequeñas sin negociar.
                        </li>
                        <li>
                          <b>Altar Firme:</b> 14 días registrando evidencia.
                        </li>
                        <li>
                          <b>Identidad:</b> Semana 3 completada.
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="missionSealsGrid">
                  {authoritySeals.map((seal) => (
                    <div
                      key={seal.key}
                      className={`missionSealCard ${
                        seal.unlocked ? "is-unlocked" : "is-locked"
                      }`}
                      title={seal.requirement}
                    >
                      <div className="missionSealMedal">
                        <div className="missionSealMedalInner">{seal.short}</div>
                      </div>

                      <div className="missionSealContent">
                        <strong>{seal.name}</strong>
                        <span>
                          {seal.unlocked ? "Obtenido" : "Bloqueado"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="missionClosingNote">
                Tu constancia está acumulando lo necesario para la apertura del Protocolo
                Final al concluir los 28 días.
              </p>
            </div>
          )}

          {validationError && (
            <div className="missionValidationError">
              {validationError}
            </div>
          )}
          
          <div className="missionActions">
            <button
              type="button"
              className="missionBtn missionBtn--ghost"
              onClick={() => setStep((value) => Math.max(value - 1, 0))}
              disabled={step === 0}
            >
              Atrás
            </button>

            {isLast ? (
              <button
                type="button"
                className="missionBtn missionBtn--primary"
                onClick={() => {
                  setSavingMission(false);
                  onClose();
                }}
              >
                Volver al día
              </button>
            ) : (
              <button
                type="button"
                className="missionBtn missionBtn--primary"
                onClick={handlePrimaryAction}
                disabled={savingMission}
              >
                {savingMission ? "Guardando..." : getPrimaryButtonText()}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  </div>
);
}